import { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { useTranslations } from '@components/context';
import moment from 'moment';
import $ from 'jquery';
import "./index.less";
import classNames from 'classnames';


const pad2 = (n) => String(n).padStart(2, '0');

const MINUTE_STEP = 5;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTE_OPTIONS = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => pad2(i * MINUTE_STEP));

function minuteFlooredToStep(m) {
    return Math.floor(m.minute() / MINUTE_STEP) * MINUTE_STEP;
}

function draftFromMoment(m) {
    return {
        date: m.clone().startOf('day').toDate(),
        hour: pad2(m.hour()),
        minute: pad2(minuteFlooredToStep(m)),
    };
}

function momentFromValue(value) {
    if (!value) return null;
    if (typeof window !== 'undefined' && window.moment?.tz && typeof window.get_timezone === 'function') {
        const m = window.moment.tz(value, window.get_timezone());
        return m.isValid() ? m : null;
    }
    const m = moment(value);
    return m.isValid() ? m : null;
}

function parseDatetime(value) {
    const m = momentFromValue(value) || moment();
    return draftFromMoment(m);
}

const DatepickerV2 = ({ value, onChange, config = {}, show = false }) => {
    const { t } = useTranslations();
    const rootRef = useRef(null);
    const containerRef = useRef(null);
    const hourRef = useRef(null);
    const minuteRef = useRef(null);
    const [dateTime, setDateTime] = useState(() => parseDatetime(value));
    const { clearable = true, clearLabel = 'Clear' } = config;

    const syncFromValue = useCallback((nextValue) => {
        const draft = parseDatetime(nextValue);
        setDateTime(draft);

        const calEl = containerRef.current;
        if (calEl && $(calEl).hasClass('hasDatepicker')) {
            const m = momentFromValue(nextValue);
            if (m) {
                $(calEl).datepicker('setDate', m.format('DD-MM-YYYY'));
            } else {
                $(calEl).datepicker('setDate', null);
            }
        }

        if (hourRef.current) {
            $(hourRef.current).val(draft.hour);
        }
        if (minuteRef.current) {
            $(minuteRef.current).val(draft.minute);
        }
    }, []);

    const setHour = useCallback((hour) => {
        const h = pad2(hour);
        setDateTime((prev) => ({ ...prev, hour: h }));
        if (hourRef.current) {
            $(hourRef.current).val(h);
        }
    }, []);

    const setMinute = useCallback((minute) => {
        const m = pad2(minuteFlooredToStep(moment().minute(minute)));
        setDateTime((prev) => ({ ...prev, minute: m }));
        if (minuteRef.current) {
            $(minuteRef.current).val(m);
        }
    }, []);

    const setDraftDate = useCallback((year, month, day) => {
        const parsed = moment({ year, month: month - 1, day });
        if (!parsed.isValid()) {
            return;
        }
        setDateTime((prev) => ({
            ...prev,
            date: parsed.clone().startOf('day').toDate(),
        }));
        const calEl = containerRef.current;
        if (calEl && $(calEl).hasClass('hasDatepicker')) {
            $(calEl).datepicker('setDate', parsed.toDate());
        }
    }, []);

    const handleCalendarSelect = useCallback((dateStr) => {
        setDateTime((prev) => {
            if (!dateStr) {
                return { ...prev, date: null };
            }
            return {
                ...prev,
                date: moment(dateStr, ['DD-MM-YYYY', 'DD-MM-YY'], true).startOf('day').toDate(),
            };
        });
    }, []);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        $(el).datepicker({
            showButtonPanel: false,
            dateFormat: 'dd-mm-yy',
            onSelect: handleCalendarSelect,
        });

        return () => {
            $(el).datepicker('destroy');
        };
    }, [handleCalendarSelect]);

    useLayoutEffect(() => {
        syncFromValue(value);
    }, [value, syncFromValue]);

    useLayoutEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        root.setDraftDate = setDraftDate;
        root.setHour = setHour;
        root.setMinute = setMinute;

        return () => {
            delete root.setDraftDate;
            delete root.setHour;
            delete root.setMinute;
        };
    }, [setHour, setMinute, setDraftDate]);

    const handleApply = useCallback(() => {
        const { date, hour, minute } = dateTime;
        if (!date) {
            return;
        }
        const day = moment(date).startOf('day');
        const wallDatetime = `${day.format('YYYY-MM-DD')} ${hour}:${minute}:00`;
        onChange(wallDatetime);
    }, [dateTime, onChange]);

    const stopPropagation = useCallback((e) => {
        e.stopPropagation();
    }, []);

    return (
        <div ref={rootRef} className={classNames("datepicker-v2", { show })}>
            <div
                ref={containerRef}
                onClick={stopPropagation}
                className="calendar-navigation-datepicker datepicker-without-bg-layout datepicker-v2__cal"
            />
            <div className="datepicker-v2__time" onClick={stopPropagation}>
                <label className="datepicker-v2__time-field">
                    <span className="datepicker-v2__time-label">{t('Hour')}</span>
                    <select
                        ref={hourRef}
                        className="datepicker-v2__time-select datepicker-v2__hour-select"
                        value={dateTime.hour}
                        onChange={(e) =>
                            setDateTime((prev) => ({ ...prev, hour: e.target.value }))
                        }
                    >
                        {HOUR_OPTIONS.map((h) => (
                            <option key={h} value={h}>
                                {h}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="datepicker-v2__time-field">
                    <span className="datepicker-v2__time-label">{t('Minute')}</span>
                    <select
                        ref={minuteRef}
                        className="datepicker-v2__time-select datepicker-v2__minute-select"
                        value={dateTime.minute}
                        onChange={(e) =>
                            setDateTime((prev) => ({ ...prev, minute: e.target.value }))
                        }
                    >
                        {MINUTE_OPTIONS.map((m) => (
                            <option key={m} value={m}>
                                {m}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="datepicker-v2__actions">
                {clearable && (
                    <button
                        type="button"
                        className="btn datepicker-v2__clear-btn"
                        onClick={() => onChange(null)}
                    >
                        {t(clearLabel)}
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-primary datepicker-v2__apply-btn"
                    onClick={handleApply}
                    disabled={!dateTime.date}
                >
                    {t('Apply')}
                </button>
            </div>
        </div>
    );
};

export default DatepickerV2;
